import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

/**
 * Public account-deletion page (`/delete-account`).
 *
 * This is the URL declared in the Google Play Data safety form, which requires
 * it to be reachable without signing in, without installing the app, and to
 * carry three things: the app and developer name, the steps to request
 * deletion, and what data is deleted versus kept.
 *
 * It is deliberately separate from `/settings/delete-account`, which is the
 * signed-in page that performs the deletion — that one redirects to login when
 * signed out, so it cannot serve as the public URL.
 *
 * Every claim below was checked against the deletion path rather than written
 * from intent: `accounts/views.py::delete_account` cascades the user and, only
 * when no other active account shares the family, the family too. A large number
 * of rows can survive it because their user reference is SET_NULL — community
 * plans, activity comments and votes, plan invites, pod
 * rows, some shared-household content, the intel mailing-list subscription — and
 * several of those carry contact details of their own. Survival is conditional,
 * not guaranteed: content hanging off the departing user's FamilyMember row
 * cascades away with it, which is why section 4 says "may remain".
 *
 * Section 4 therefore describes CATEGORIES, not rows. Play asks for the types of
 * data kept, and an inventory of models would be both unreadable and stale within
 * a release. The rule when editing: a new retained model needs a new bullet only
 * if it does not already fall inside one of the categories, but if it retains a
 * kind of personal data none of them mention — a new contact field, a new
 * identifier — section 4 must gain it. Section 5's erasure promise deliberately
 * excludes backups, logs, and legally required records; do not widen it without
 * checking those exclusions still hold.
 */
const DeleteAccountRequest: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="legal-back">&larr; Back to VizDay</Link>
        <h1>Delete your VizDay account</h1>
        <p className="legal-meta">VizDay, by VizDNA LLC · Last updated: September 18, 2026</p>

        <p>
          You can delete your VizDay account and its data at any time, either from inside the
          app or by emailing us. Both routes are described below, along with what is removed,
          what is left behind, and how to have the remainder erased too.
        </p>

        <h2>1. Delete your account from the app</h2>
        <p>This is the fastest route, and it takes effect as soon as you confirm.</p>
        <ul>
          <li><strong>On Android or iOS:</strong> open VizDay, tap the gear icon to open Settings, scroll to the bottom and tap <strong>Delete account</strong>. Confirm when prompted.</li>
          <li><strong>On the web:</strong> sign in at <a href="https://www.vizday.com">vizday.com</a>, open Settings, then <strong>Delete account</strong>. Type <strong>DELETE</strong> to confirm.</li>
        </ul>
        <p>
          Deletion is permanent and cannot be undone. If it does not complete — an error, or the
          app reports a failure — email us at{' '}
          <a href="mailto:arnold@vizday.com">arnold@vizday.com</a> and we will finish it by hand.
        </p>

        <h2>2. Request deletion by email</h2>
        <p>
          If you cannot sign in — a lost password, a closed email address, or a device you no
          longer have — email{' '}
          <a href="mailto:arnold@vizday.com?subject=Delete%20my%20VizDay%20account">arnold@vizday.com</a>{' '}
          with the subject <strong>Delete my VizDay account</strong>.
        </p>
        <ul>
          <li>Send it from the email address on the account where possible. If you cannot, tell us the address the account uses so we can identify it.</li>
          <li>We will confirm the request and complete the deletion within 30 days.</li>
          <li>You do not need to install the app to make this request.</li>
        </ul>

        <h2>3. What is deleted</h2>
        <p>Deleting your account permanently removes:</p>
        <ul>
          <li>Your profile, name, email address, and login credentials, including any Google or Apple sign-in link.</li>
          <li>Your trips, itineraries, activities, notes, and saved guides.</li>
          <li>Your conversations with the AI planner.</li>
          <li>Your connected calendars and every event synced from them, along with the stored access tokens.</li>
          <li>Your notification preferences and other account settings.</li>
          <li>Your collections and the plans you saved from other people.</li>
          <li>
            Your household record, including its home location — but only if yours is the last
            VizDay account attached to it. If you share a household with someone whose account is
            still active, the household and its home location stay with them, and only your own
            membership of it is removed.
          </li>
        </ul>

        <h2>4. What is left behind</h2>
        <p>
          Some records outlive the account. Below are the kinds of records that remain and why.
          This describes categories rather than listing every row in our database, and several of
          these records keep details you entered at the time, so we do not describe what remains
          as anonymous.
        </p>
        <ul>
          <li>
            <strong>Things you shared with other people.</strong> Plans you published to the
            community, comments and votes you left, contributions to plans and groups you were
            part of, and replies you sent stay in place, so the people still using them do not
            lose them. Your account is detached from them.
            Some of these keep what you typed at the time — the email address or phone number an
            invitation was sent to, the name and contact details on a reply, the text of an update
            you wrote — and some carry an internal identifier derived from your account number.
          </li>
          <li>
            <strong>A household someone else still uses.</strong> If another active VizDay account
            belongs to the household you were part of, the household stays with them, including its
            home location. Your membership of it is removed, and entries attached to you personally
            — notes and memories filed against your member profile — go with it. Entries you added
            that belong to the household rather than to you, such as shared reminders, may remain,
            with your name removed.
          </li>
          <li>
            <strong>Our travel-intel mailing list.</strong> If you subscribed, your email address
            and the answers you gave to the Match Me questions stay on the list. Use the
            unsubscribe link in any digest, or ask us.
          </li>
          <li>
            <strong>Product analytics.</strong> We use PostHog to understand how VizDay is used;
            where those records are linked to you they carry your account number, email address,
            and first name. We also keep in-product usage records of our own. Deleting your
            account does not erase either on its own.
          </li>
          <li>
            <strong>Application and security logs.</strong> Our logs record actions taken on the
            service, including the deletion itself, and can contain your account number, email
            address, household identifier, IP address, and browser or device identifier. They are
            kept on a rolling basis for security and troubleshooting and age out on their own
            schedule; individual entries cannot be picked out and removed.
          </li>
          <li>
            <strong>Encrypted database backups.</strong> Deleted data can persist in routine
            backups until those backups age out on their normal rotation, after which it is gone
            from those too. Backups are not used to restore deleted accounts.
          </li>
          <li>
            <strong>Records we are required to keep.</strong> Where law requires us to retain
            something — a payment record, for example — we keep only that record, for only as long
            as required.
          </li>
        </ul>

        <h2>5. Having the remainder erased</h2>
        <p>
          <strong>The best time to ask is before you delete.</strong> While the account still
          exists, everything above is linked to it and we can act on all of it. Email{' '}
          <a href="mailto:arnold@vizday.com?subject=Erase%20my%20remaining%20VizDay%20data">arnold@vizday.com</a>,
          say what you want removed, and delete the account afterwards.
        </p>
        <p>
          After the account is gone we can still erase whatever remains traceable to you: your
          mailing-list entry, your analytics record, a published plan you name, photos
          you uploaded, invitations or replies carrying the email address, phone number, or name
          you gave, and the comments and votes you left on a group plan, which carry an identifier
          derived from your account number.
          Tell us what to remove and we will confirm within 30 days.
        </p>
        <p>
          What we cannot do afterwards is find records whose link to your account was already cut
          and which carry no other trace of you — a vote you left on a public community plan, for
          example.
          Detaching your account is what keeps shared content working for the people still using
          it, and for records like these it leaves nothing pointing back to you, so we cannot pick
          them out.
        </p>
        <p>
          Three things we cannot erase on request at all, for the reasons given above: routine
          encrypted backups, which age out on their own rotation; application and security logs,
          which are not individually editable; and records the law requires us to keep. Nothing in
          a shared household that belongs to another active account can be deleted at your request
          either — ask them.
        </p>

        <h2>6. Deleting some data without deleting your account</h2>
        <p>
          You do not have to delete your whole account to remove data. Inside the app you can
          delete individual trips and plans; a deleted plan is erased permanently on the first
          daily clean-up after it has been deleted for 30 days, so within 31 days.
        </p>
        <p>
          You can also switch off a connected calendar, which stops its events appearing in
          VizDay. Switching a calendar off hides its events rather than erasing them, so if you
          want the synced calendar data removed altogether, email{' '}
          <a href="mailto:arnold@vizday.com">arnold@vizday.com</a> and we will delete it.
        </p>

        <h2>7. Questions</h2>
        <p>
          Email <a href="mailto:arnold@vizday.com">arnold@vizday.com</a> and we will help. For the
          full picture of what we collect and why, see our Privacy Policy.
        </p>

        <div className="legal-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountRequest;
